/**
 * src/lib/standingText.ts
 * ------------------------------------------------------------------
 * SAYING THE SAME THING IN EVERY PLACE IT IS SAID.
 *
 * A pupil's standing is now one figure (`accountStanding` in financeCore).
 * It is about to be shown in at least four places - the parent portal, the
 * fees list, the invoice PDF and the reminder emails - and if each of them
 * writes its own sentence they will disagree within a term. They will
 * disagree about the sign first: "balance -24,000" is a credit to an
 * accountant and a debt to everybody else, and the parents who complained
 * were reading screens written by people who did not agree about that.
 *
 * So the wording lives here, next to the arithmetic, and is tested like it.
 * Nothing here computes anything - it takes a Standing and returns the words.
 *
 * Two audiences, because they are owed different sentences. A parent is
 * addressed ("You are P24,000.00 in credit"); a bursar is shown a column
 * that has to stay narrow ("In credit 24,000.00"). Same figure, same sign,
 * same rounding - only the voice differs.
 * ------------------------------------------------------------------ */
import { fmtPula } from './hrPayroll';
import { round2, OPENING_BALANCE_LABEL, type Standing } from './financeCore';

export type StandingTone = 'owing' | 'credit' | 'settled' | 'none';

export interface StandingLines {
  /** The whole thing as one sentence, for a headline. */
  headline: string;
  /** Just the money, for a figure beside a label. Never negative: the tone
   *  carries the direction, so no screen has to decide what a minus means. */
  amount: string;
  /** What the figure is, for colour and emphasis. */
  tone: StandingTone;
  /**
   * The carried-forward part, when there is one. Absent rather than empty,
   * so `{lines.broughtForward && <p>…}` renders nothing instead of a gap.
   */
  broughtForward?: string;
}

export interface StandingTextInput extends Standing {
  /** From AccountStanding. 0 means no fees raised, which is not "settled". */
  documentCount?: number;
}

/**
 * Turn a standing into the words for it.
 *
 * `asAt` is deliberately not mentioned in the headline. A parent reading
 * "You owe P12,000.00 as at 22 September" starts wondering what changed
 * since; the screen states the date once, in its own furniture.
 */
export function standingLines(
  st: StandingTextInput,
  audience: 'parent' | 'staff' = 'parent',
): StandingLines {
  const parent = audience === 'parent';
  const balance = round2(st.balance);
  const money = (n: number) => `P${fmtPula(Math.abs(n))}`;

  let headline: string;
  let tone: StandingTone;

  if (st.documentCount === 0) {
    // Nothing has been billed. Saying "settled" would imply it had been.
    tone = 'none';
    headline = parent ? 'No fees have been raised yet' : 'No fees raised';
  } else if (balance > 0) {
    tone = 'owing';
    headline = parent ? `You owe ${money(balance)}` : `Owes ${money(balance)}`;
  } else if (balance < 0) {
    tone = 'credit';
    headline = parent
      ? `You are ${money(balance)} in credit`
      : `In credit ${money(balance)}`;
  } else {
    tone = 'settled';
    headline = parent ? 'Your account is settled' : 'Settled';
  }

  const out: StandingLines = {
    headline,
    amount: tone === 'none' ? money(0) : money(balance),
    tone,
  };

  /* THE SENTENCE THE OFFICE KEEPS SAYING ON THE TELEPHONE. Without it, a
     parent who settled Term 2 in July sees a Term 3 invoice for P22,000 and
     an account balance of P30,000 and concludes the school has lost a
     payment. With it, the P8,000 has a date and an origin. */
  const bf = round2(st.broughtForward);
  if (bf > 0) {
    out.broughtForward = parent
      ? `Includes ${money(bf)} carried forward from ${OPENING_BALANCE_LABEL}`
      : `${money(bf)} brought forward`;
  } else if (bf < 0) {
    out.broughtForward = parent
      ? `After ${money(bf)} of credit carried forward from ${OPENING_BALANCE_LABEL}`
      : `${money(bf)} credit brought forward`;
  }

  return out;
}

/**
 * The two lines an invoice has to carry side by side.
 *
 * This is the whole complaint in one function. A parent was shown a Term 3
 * invoice for P22,000 with no mention of the P24,000 the school was already
 * holding for them, so the invoice read as a demand. An invoice must still
 * state the term's fee in full - netting it would understate fee income
 * permanently - so the account goes BESIDE it rather than inside it.
 */
export function invoiceFooterLines(
  invoiceTotal: number,
  invoiceBalance: number,
  st: StandingTextInput,
): { thisInvoice: string; thisInvoiceDue: string; account: string; note?: string } {
  const money = (n: number) => `P${fmtPula(Math.abs(n))}`;
  const lines = standingLines(st, 'parent');
  const owesElsewhere = round2(st.balance) !== round2(invoiceBalance);
  return {
    thisInvoice: money(invoiceTotal),
    thisInvoiceDue: money(invoiceBalance),
    account: lines.headline,
    /* Only worth saying when the two differ. On a pupil whose only document
       is this invoice, "your account balance is the same number again" is
       noise, and noise on an invoice is what gets invoices ignored. */
    note: owesElsewhere
      ? (round2(st.balance) < round2(invoiceBalance)
        ? 'Your account already holds credit against this invoice.'
        : 'Your account includes amounts from earlier invoices.')
      : undefined,
  };
}
